/**
 * Shaping a metric series before it is drawn or described.
 *
 * Its own module rather than an export from `Sparkline.tsx`, because a file that exports both a
 * component and a plain function loses Fast Refresh — the whole module reloads on every edit
 * instead of the component re-rendering in place.
 */

/**
 * The series from its first real reading onwards.
 *
 * CPU is a rate, so it cannot be worked out from a server's very first sample — there is nothing to
 * compare against — and that sample is stored with a null. Leading nulls therefore mean "the window
 * starts before this server had usable data", not "a gap in the middle of it".
 *
 * Drawn as-is, that reserved the left of the box for a period the server did not exist for. A
 * server added ten minutes ago has three samples, the first null, and its chart rendered as a line
 * across the right half of a full-width box beside a neighbour whose line spanned the whole thing —
 * which reads as a broken chart rather than as a shorter history.
 *
 * Trimming them lets the readings that do exist use the full width. Interior gaps still break the
 * line: those sit between two known points, and interpolating across them would draw a confident
 * line through a period nobody observed.
 */
export function fromFirstReading(values: (number | null)[]): (number | null)[] {
  const first = values.findIndex((value) => value !== null);

  return first <= 0 ? values : values.slice(first);
}
